// src/components/Clients.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  orderBy,
  arrayUnion
} from 'firebase/firestore';
import { FaPlus, FaEdit, FaSearch, FaTimes } from 'react-icons/fa';
import '../styles/Clients.css'; // Ensure this CSS file exists and is styled appropriately

const pageSize = 10;

// --- Helper Functions ---

// Formata CNPJ: XX.XXX.XXX/XXXX-XX
const formatCNPJ = (cnpj) => {
  if (!cnpj) return '';
  const digits = cnpj.replace(/\D/g, '').padStart(14, '0');
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

// Formata número para R$ xx.xxx,xx
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '';
  const num = Number(value);
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Converte string R$xx.xxx,xx para number
const parseCurrency = (str) => {
  if (str == null || typeof str !== 'string') return 0;
  const raw = str.replace(/[R$\s.]/g, '').replace(',', '.'); // Handle R$, spaces, dots, and comma decimal
  return raw ? parseFloat(raw) : 0;
};

// Formata Data YYYY-MM-DD para DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return '';
  // Handles cases where date might already be formatted or has time
  if (dateString.includes('/')) return dateString;
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return dateString; // Return original if parse fails
    // Create date in UTC to avoid timezone issues with simple date strings
    const date = new Date(Date.UTC(year, month - 1, day));
    if (isNaN(date.getTime())) return dateString; // Invalid date
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return dateString; // Return original on error
  }
};


// Mapeia status a classes CSS (sem acentos)
const statusClassMap = {
  Ativo: 'status-ativo',
  'Em Renegociação': 'status-emrenegociacao', // Note: 'Em Renegociacao' without accent for CSS class name safety
  Vencido: 'status-vencido',
  Churn: 'status-churn',
};

// Lista de produtos disponíveis
const AVAILABLE_PRODUCTS = [
  'CX Platform',
  'Customer Insights',
  'Experience Awards',
  'Consultoria',
  'Employee Experience',
];

// --- Component ---

const Clients = () => {
  const [empresaId, setEmpresaId] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [cnpjError, setCnpjError] = useState(null);

  // --- Initial Form State ---
  const initialProductData = AVAILABLE_PRODUCTS.reduce((acc, prod) => {
    const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4); // e.g., cxpl, cust, expe, cons, empl
    acc[prod] = {
      [`${prefix}_linkContrato`]: '',
      [`${prefix}_dataVencimento`]: '',
      [`${prefix}_status`]: 'Ativo', // Default status per product
      [`${prefix}_revisadoClausulas`]: 'Não',
      [`${prefix}_tipoRenovacao`]: 'Aditivo',
      [`${prefix}_indiceRenovacao`]: 'IPCA',
      [`${prefix}_competenciaPrimeiroFaturamento`]: 'Janeiro',
      [`${prefix}_formaPagamento`]: 'Boleto',
      [`${prefix}_condicaoPagamentoTipo`]: 'Todo dia X',
      [`${prefix}_condicaoPagamentoValor`]: '',
      [`${prefix}_emailsNF`]: '',
      [`${prefix}_outrasInfosEnvio`]: '',
      [`${prefix}_tipoPrecificacao`]: 'PRECIFICAÇÃO FIXA',
      [`${prefix}_valorTicket`]: 0,
      [`${prefix}_formatoContratacao`]: 'Resposta',
      [`${prefix}_volumeResposta`]: '',
      [`${prefix}_quantidadeMinMax`]: '',
      [`${prefix}_valorRespostaAdicional`]: 0,
      [`${prefix}_limiteOrcamento`]: 'Não',
      [`${prefix}_TagSense`]: 'Não', // Applies to CX, CI, EE
      [`${prefix}_tipoTagSense`]: 'Padrão', // Applies to CX, EE
      [`${prefix}_modeloTagSense`]: '3.5', // Applies to CX, EE
      [`${prefix}_volumeMensalComentarios`]: '', // Applies to CX, EE
      [`${prefix}_valorTagSense`]: 0, // Applies to CX, EE
      [`${prefix}_setup`]: 'Não', // Applies to CX, CI, EE
      [`${prefix}_valorSetup`]: 0, // Applies to CX, CI, EE
      [`${prefix}_dataEmissaoSetup`]: '', // Applies to CX, CI, EE
      // Experience Awards specific
      [`${prefix}_pacote`]: 'Report',
      [`${prefix}_tipoPagamento`]: 'Pagamento Único',
      [`${prefix}_valor`]: 0,
      [`${prefix}_parcelas`]: 1,
      [`${prefix}_stakeholders`]: '',
      [`${prefix}_categoriasLiberadas`]: '',
      [`${prefix}_anosLiberados`]: '',
      [`${prefix}_quemReceberaAcesso`]: '',
      // Consultoria specific (overlaps with EA payment)
      // [`${prefix}_tipoPagamento`]: 'Pagamento Único', // Already covered
      // [`${prefix}_valor`]: 0, // Already covered
      // [`${prefix}_parcelas`]: 1, // Already covered
    };
    // Set defaults for fields that might inherit from CX Platform
    if (prod !== 'CX Platform') {
        acc[prod][`${prefix}_tipoRenovacao`] = 'Igual a CX Platform';
        acc[prod][`${prefix}_indiceRenovacao`] = 'Igual a CX Platform';
        acc[prod][`${prefix}_competenciaPrimeiroFaturamento`] = 'Igual a CX Platform';
        acc[prod][`${prefix}_formaPagamento`] = 'Igual a CX Platform';
        acc[prod][`${prefix}_condicaoPagamentoTipo`] = 'Igual a CX Platform';
        acc[prod][`${prefix}_emailsNF`] = 'Igual a CX Platform';
        acc[prod][`${prefix}_outrasInfosEnvio`] = 'Igual a CX Platform';
    }
     // Set unique condition types if needed (override default)
    if (prod === 'Customer Insights') {
        acc[prod][`${prefix}_condicaoPagamentoTipo`] = 'Todo dia X';
    }
    if (prod === 'Experience Awards') {
        acc[prod][`${prefix}_condicaoPagamentoTipo`] = 'Todo dia X';
    }
    if (prod === 'Consultoria') {
        acc[prod][`${prefix}_condicaoPagamentoTipo`] = 'Todo dia X';
    }
     if (prod === 'Employee Experience') {
        acc[prod][`${prefix}_condicaoPagamentoTipo`] = 'Todo dia X';
    }

    return acc;
  }, {});


  const initialForm = {
    // Cliente
    name: '',
    alias: '',
    cnpj: '',
    segmento: '',
    origem: '',
    classificacao: '',
    slugInstancia: '',
    token: '',
    apitoken: '',
    // Responsável pela informação (empresa)
    respEmpresaNome: '',
    respEmpresaEmail: '',
    respEmpresaArea: '',
    respEmpresaCargo: '',
    respEmpresaMaturidade: 'Básico',
    // Produtos - Array of selected product names
    produtos: [], // e.g., ['CX Platform', 'Consultoria']
    // Product Specific Data - Object keyed by product name
    productsData: initialProductData,
    // General Info (Consider if these are still needed or derived from products)
    // startDate: '', // No longer used directly in table
    // endDate: '', // Use product-specific vencimento
    // mensalValue: 0, // Value might be per product now
    // status: 'Ativo', // Use product-specific status
    responsavelCS: '',
    responsavelComercial: '',
    historicoContratos: [], // Keep for general contract links/notes
    historicoUpdates: [],
    // Endereço
    street: '',
    number: '',
    district: '',
    details: '',
    city: '',
    state: '',
    zip: '',
    // Horas (Keep if they are global/shared)
    hs_csQuantidade: '',
    hs_csPeriodicidade: 'Mês',
    hs_psQuantidade: '',
    hs_psPeriodicidade: 'Mês'
  };
  const [form, setForm] = useState(initialForm);
  const user = auth.currentUser;

  // --- Label Map for History ---
  const labelMap = {
    cnpj: 'CNPJ',
    name: 'Razão Social',
    alias: 'Nome Fantasia',
    segmento: 'Segmento',
    origem: 'Origem',
    classificacao: 'Classificação',
    slugInstancia: 'Slug da Instância',
    token: 'Token',
    apitoken: 'API Token',
    respEmpresaNome: 'Responsável (Nome)',
    respEmpresaEmail: 'Responsável (E-mail)',
    respEmpresaArea: 'Área',
    respEmpresaCargo: 'Cargo',
    respEmpresaMaturidade: 'Maturidade',
    responsavelCS: 'Responsável CS',
    responsavelComercial: 'Responsável Comercial',
    historicoContratos: 'Contratos',
    street: 'Endereço',
    number: 'Número',
    district: 'Bairro',
    details: 'Complemento',
    city: 'Cidade',
    state: 'Estado',
    zip: 'CEP',
    hs_csQuantidade: 'Horas CS',
    hs_csPeriodicidade: 'Periodicidade CS',
    hs_psQuantidade: 'Horas PS',
    hs_psPeriodicidade: 'Periodicidade PS',
    produtos: 'Produtos Contratados',
    linkContrato: 'Link do Contrato',
    dataVencimento: 'Data de Vencimento',
    status: 'Status',
    revisadoClausulas: 'Revisado Cláusulas',
    tipoRenovacao: 'Tipo de Renovação',
    indiceRenovacao: 'Índice de Renovação',
    competenciaPrimeiroFaturamento: 'Competência 1º Faturamento',
    formaPagamento: 'Forma de Pagamento',
    condicaoPagamentoTipo: 'Condição Pagamento – Tipo',
    condicaoPagamentoValor: 'Condição Pagamento – Valor',
    emailsNF: 'E-mails para Envio da NF',
    outrasInfosEnvio: 'Outras Informações Envio NF',
    tipoPrecificacao: 'Tipo de Precificação',
    valorTicket: 'Valor do Ticket',
    formatoContratacao: 'Formato de Contratação',
    volumeResposta: 'Volume Contratado',
    quantidadeMinMax: 'Qtd. Mín./Máx.',
    valorRespostaAdicional: 'Valor Adicional',
    limiteOrcamento: 'Limite de Orçamento',
    TagSense: 'Tem TagSense?',
    tipoTagSense: 'Tipo de TagSense',
    modeloTagSense: 'Modelo de TagSense',
    volumeMensalComentarios: 'Volume Mensal de Comentários',
    valorTagSense: 'Valor da TagSense',
    setup: 'Tem Setup?',
    valorSetup: 'Valor do Setup',
    dataEmissaoSetup: 'Data Emissão do Setup',
    pacote: 'Pacote',
    tipoPagamento: 'Tipo de Pagamento',
    valor: 'Valor',
    parcelas: 'Parcelas',
    stakeholders: 'Stakeholders',
    categoriasLiberadas: 'Categorias Liberadas',
    anosLiberados: 'Anos Liberados',
    quemReceberaAcesso: 'Quem Receberá Acesso?'
  };
  

  const formatChangeValue = (key, val) => {
     // Handle nested product data keys for history
     if (key.startsWith('productsData.')) {
        const parts = key.split('.');
        const product = parts[1];
        const fieldKey = parts[2];
        // Check if it's a known currency field
        if (fieldKey.endsWith('Ticket') || fieldKey.endsWith('valor') || fieldKey.endsWith('Adicional') || fieldKey.endsWith('Setup') || fieldKey.endsWith('TagSense')) {
          return formatCurrency(val);
        }
        // Check if it's a known date field
        if (fieldKey.endsWith('Vencimento') || fieldKey.endsWith('EmissaoSetup')) {
          return formatDate(val);
        }
        // If it's the 'produtos' array itself
         if (key === 'produtos' && Array.isArray(val)) {
            return val.join(', ');
        }
        // Add more specific formatting if needed for other product fields
     } else {
        // Handle top-level fields
        if (key === 'mensalValue') { // Keep formatting if mensalValue is still used somewhere
          return formatCurrency(val);
        }
        if (key === 'startDate' || key === 'endDate') { // Keep formatting if these are still used
          return formatDate(val);
        }
        if (key === 'cnpj') return formatCNPJ(val);
        if (key === 'historicoContratos' && Array.isArray(val)) return val.join(', ');
     }

     // Default return
     return (val !== undefined && val !== null) ? String(val) : '';
  };

  // --- Effects ---
  useEffect(() => {
    if (!user) return;
    (async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (snap.exists()) setEmpresaId(snap.data().empresa_id);
    })();
  }, [user]);

  useEffect(() => {
    if (!empresaId) return;
    (async () => {
      const col = collection(db, 'Empresas', empresaId, 'Clientes');
      const snap = await getDocs(query(col, orderBy('name')));
      let list = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        // Ensure nested data structures are present
        produtos: d.data().produtos || [],
        productsData: d.data().productsData || initialProductData, // Default if missing
        historicoUpdates: d.data().historicoUpdates || []
      }));

      // Filter by department if necessary
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      const dep = userSnap.data()?.departamento;
      if (dep === 'CS') list = list.filter(c => c.responsavelCS === user?.email);
      if (dep === 'Comercial') list = list.filter(c => c.responsavelComercial === user?.email);

      setClients(list);
    })();
  }, [empresaId, user]); // Rerun when user changes too

  // --- Filtering and Pagination ---
  const filtered = clients.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.cnpj || '').replace(/\D/g, '').includes(searchTerm.replace(/\D/g, ''))
  );
  const pageCount = Math.max(Math.ceil(filtered.length / pageSize), 1);
  const pageItems = filtered.slice(page * pageSize, (page + 1) * pageSize);

  // --- Status Calculation ---
  const computeProductStatus = (productName, clientData) => {
     const productKey = productName.toLowerCase().split(' ')[0].substring(0, 4);
     const productInfo = clientData.productsData?.[productName];

     if (!productInfo) return 'N/A'; // Product data missing

     const endDate = productInfo[`${productKey}_dataVencimento`];
     const currentStatus = productInfo[`${productKey}_status`] || 'Ativo'; // Fallback status

     if (!endDate) return currentStatus; // If no end date, return current status

     try {
         const today = new Date();
         today.setHours(0, 0, 0, 0); // Compare dates only
         const end = new Date(endDate); // Assumes YYYY-MM-DD from date input
         if (isNaN(end.getTime())) return currentStatus; // Invalid date stored

         // Create date in UTC to avoid timezone issues with comparisons
         const endUTC = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));

         if (today > endUTC) {
             return 'Vencido';
         } else {
             return currentStatus; // Return stored status if not vencido
         }
     } catch (e) {
         console.error("Error computing status for:", productName, endDate, e);
         return currentStatus; // Fallback on error
     }
 };


  // --- Modal Handling ---
  const openNew = () => {
    setForm(JSON.parse(JSON.stringify(initialForm))); // Deep copy initial form
    setIsEdit(false);
    setCnpjError(null);
    setModalOpen(true);
  };

  const openEdit = (client) => {
    // Deep copy client data and merge with initialForm structure to ensure all fields exist
    const editForm = JSON.parse(JSON.stringify(initialForm));
    const clientCopy = JSON.parse(JSON.stringify(client));

    // Merge top-level fields
    Object.keys(editForm).forEach(key => {
        if (clientCopy[key] !== undefined) {
            editForm[key] = clientCopy[key];
        }
    });

     // Ensure productsData is properly populated, merging existing with defaults
     AVAILABLE_PRODUCTS.forEach(prod => {
        if (!editForm.productsData[prod]) {
            editForm.productsData[prod] = initialProductData[prod]; // Add missing product structure
        }
        if (clientCopy.productsData && clientCopy.productsData[prod]) {
             // Merge fields from saved data into the structure
            Object.keys(editForm.productsData[prod]).forEach(fieldKey => {
               if (clientCopy.productsData[prod][fieldKey] !== undefined) {
                  editForm.productsData[prod][fieldKey] = clientCopy.productsData[prod][fieldKey];
               }
            });
        }
     });


    setSelected(client); // Keep original selected for comparison during save
    setForm(editForm);
    setIsEdit(true);
    setCnpjError(null);
    setModalOpen(true);
  };

  // --- Form Input Handlers ---

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm(prevForm => ({ ...prevForm, [name]: value }));
  };

  const handleProductChange = (e) => {
    const { name, value, dataset } = e.target;
    const { product } = dataset; // Get product name from data-product attribute

    if (!product) return; // Should not happen if data-product is set

    setForm(prevForm => {
        const updatedProductsData = JSON.parse(JSON.stringify(prevForm.productsData)); // Deep copy
        if (!updatedProductsData[product]) {
             updatedProductsData[product] = {}; // Initialize if somehow missing
        }
        updatedProductsData[product][name] = value;

        // Auto-update status if date changes (optional, but can be helpful)
        /*
        const productKey = product.toLowerCase().split(' ')[0].substring(0, 4);
        if (name === `${productKey}_dataVencimento`) {
            const newStatus = computeProductStatus(product, { productsData: updatedProductsData });
            updatedProductsData[product][`${productKey}_status`] = newStatus;
        }
        */

        return { ...prevForm, productsData: updatedProductsData };
    });
};


const handleProductCurrencyChange = (e) => {
    const { name, value, dataset } = e.target;
    const { product } = dataset;

    if (!product) return;

    const numericValue = parseCurrency(value);

    setForm(prevForm => {
        const updatedProductsData = JSON.parse(JSON.stringify(prevForm.productsData));
         if (!updatedProductsData[product]) {
             updatedProductsData[product] = {}; // Initialize if somehow missing
        }
        updatedProductsData[product][name] = numericValue;
        return { ...prevForm, productsData: updatedProductsData };
    });
};


  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseCurrency(value);
    setForm(prevForm => ({ ...prevForm, [name]: numericValue }));
  };

  const handleContractsChange = (e) => {
    const arr = e.target.value.split(',').map(u => u.trim()).filter(u => u);
    setForm({ ...form, historicoContratos: arr });
  };

   // Handle Product Checkbox Change
   const handleProductCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setForm(prevForm => {
        const currentProdutos = prevForm.produtos || [];
        let newProdutos;
        if (checked) {
            newProdutos = [...currentProdutos, value];
        } else {
            newProdutos = currentProdutos.filter(prod => prod !== value);
        }
        // Sort alphabetically for consistent display/order
        newProdutos.sort();
        return { ...prevForm, produtos: newProdutos };
    });
};


  // --- CNPJ Fetch ---
  const fetchCnpjData = async () => {
    const raw = (form.cnpj || '').replace(/\D/g, '');
    if (raw.length !== 14) { setCnpjError('CNPJ inválido (deve ter 14 dígitos)'); return; }
    setLoadingCnpj(true); setCnpjError(null);
    try {
       // Using BrasilAPI (free, no key needed, but check rate limits/terms)
       const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${raw}`);
       if (!res.ok) {
         const errorData = await res.json().catch(() => ({ message: `Erro ${res.status}` }));
         throw new Error(errorData.message || `Erro ao buscar CNPJ: ${res.status}`);
       }
       const data = await res.json();

       // Check for specific error messages from BrasilAPI
       if (data.message) {
           throw new Error(data.message);
       }

       // Map BrasilAPI fields to form fields
       setForm(f => ({
         ...f,
         name: data.razao_social || f.name,
         alias: data.nome_fantasia || f.alias,
         street: `${data.logradouro || ''}`.trim(), // Combine type and name if available, handle nulls
         number: data.numero || f.number,
         district: data.bairro || f.district,
         details: data.complemento || f.details,
         city: data.municipio || f.city,
         state: data.uf || f.state,
         zip: (data.cep || f.zip).replace(/\D/g, ''), // Clean CEP
         // Optional: You could also fetch 'cnae_fiscal_descricao' for Segmento if needed
         // segmento: data.cnae_fiscal_descricao || f.segmento,
       }));
    } catch (err) {
       console.error("CNPJ Fetch Error:", err);
       setCnpjError(`Erro ao buscar dados do CNPJ: ${err.message}`);
    } finally {
       setLoadingCnpj(false);
    }
 };


  // --- Save Client ---
  const saveClient = async () => {
     if (!empresaId || !user) {
        alert("Erro: Informações da empresa ou usuário não carregadas.");
        return;
     }

      // Basic Validation Example: Check mandatory payment value
      let validationError = false;
      form.produtos.forEach(prod => {
         const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4);
         const tipoPagamento = form.productsData[prod]?.[`${prefix}_condicaoPagamentoTipo`];
         const valorPagamento = form.productsData[prod]?.[`${prefix}_condicaoPagamentoValor`];

         if (tipoPagamento === 'Todo dia X' && (valorPagamento === '' || valorPagamento == null)) {
             alert(`Erro: Para ${prod}, o valor da condição de pagamento é obrigatório quando o tipo é 'Todo dia X'.`);
             validationError = true;
         }
      });

      if (validationError) return;


     const col = collection(db, 'Empresas', empresaId, 'Clientes');
     let changes = {};
     let dataToSave = { ...form }; // Start with the current form state

     // Ensure productsData only contains data for selected products
     const finalProductsData = {};
     (dataToSave.produtos || []).forEach(prod => {
         if (dataToSave.productsData[prod]) {
             finalProductsData[prod] = dataToSave.productsData[prod];
         }
     });
     dataToSave.productsData = finalProductsData; // Replace with filtered data

     // Clean up numeric fields that might be strings from input
     Object.keys(dataToSave).forEach(key => {
        if (key.endsWith('Value') || key.endsWith('Ticket') || key.endsWith('Adicional') ||  key.endsWith('Valor') || key === 'ea_valor' || key === 'cons_valor') {
            dataToSave[key] = Number(dataToSave[key]) || 0;
        }
        if (key === 'ea_parcelas' || key === 'cons_parcelas') {
            dataToSave[key] = Number(dataToSave[key]) || 1;
        }
     });
      // Clean up product numeric fields
     Object.keys(dataToSave.productsData).forEach(prod => {
        const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4);
        const prodData = dataToSave.productsData[prod];
        Object.keys(prodData).forEach(fieldKey => {
             if (fieldKey.endsWith('Ticket') || fieldKey.endsWith('Adicional') ||  fieldKey.endsWith('valor') || fieldKey === `${prefix}_valor`) {
                prodData[fieldKey] = Number(prodData[fieldKey]) || 0;
            }
             if (fieldKey === `${prefix}_parcelas`) {
                prodData[fieldKey] = Number(prodData[fieldKey]) || 1;
            }
        });
     });


     if (isEdit && selected) {
       // Compare current form with original selected data to log changes
       Object.keys(dataToSave).forEach(key => {
          // Simple comparison for top-level fields
          if (JSON.stringify(dataToSave[key]) !== JSON.stringify(selected[key])) {
            // Use the labelMap for better history readability
            const label = labelMap[key] || key;
            changes[label] = dataToSave[key];
          }
       });
        // Deep comparison for productsData is complex; log the whole changed object for now
        if (JSON.stringify(dataToSave.productsData) !== JSON.stringify(selected.productsData)) {
             changes['Dados dos Produtos'] = dataToSave.productsData; // Log changed product data block
        }
         if (JSON.stringify(dataToSave.produtos) !== JSON.stringify(selected.produtos)) {
             changes['Produtos Contratados'] = dataToSave.produtos; // Log changed product list
        }

     } else {
       // For new clients, log all initial data
       changes = { 'Novo Cliente Adicionado': dataToSave };
     }

     const logEntry = {
        email: user.email,
        timestamp: new Date(), // Use Firestore server timestamp for accuracy? Consider Timestamp.fromDate(new Date())
        changes: changes // Log the human-readable changes
     };

     // Prepare data for Firestore (ensure no undefined values, handle timestamps)
     const finalData = {
        ...dataToSave,
        historicoUpdates: arrayUnion(logEntry) // Add new log entry
     };

     // Remove undefined values before saving
     Object.keys(finalData).forEach(key => {
        if (finalData[key] === undefined) {
          delete finalData[key];
        }
     });


     try {
        if (isEdit && selected) {
          const ref = doc(col, selected.id);
          // Update only the fields present in finalData, plus the history
           await updateDoc(ref, finalData);
        } else {
           // Add new client, set initial commercial responsible
           const docRef = await addDoc(col, {
             ...finalData,
             responsavelComercial: user.email, // Set on creation
             historicoUpdates: [logEntry] // Initialize history
           });
           console.log("New client added with ID:", docRef.id);
        }

        setModalOpen(false);
        // Re-fetch clients to update the list
        const snap = await getDocs(query(col, orderBy('name')));
        let list = snap.docs.map(d => ({
            id: d.id,
            ...d.data(),
             produtos: d.data().produtos || [],
             productsData: d.data().productsData || initialProductData, // Default if missing
            historicoUpdates: d.data().historicoUpdates || []
          }));
        // Re-apply department filter
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const dep = userSnap.data()?.departamento;
        if (dep === 'CS') list = list.filter(c => c.responsavelCS === user?.email);
        if (dep === 'Comercial') list = list.filter(c => c.responsavelComercial === user?.email);
        setClients(list);

     } catch (error) {
        console.error("Error saving client:", error);
        alert(`Erro ao salvar cliente: ${error.message}`);
     }
  };

  // --- Render ---

  return (
    <div className="clients-wrapper">
      <div className="clients-header">
        <h2>Clientes</h2>
        <p>Gerencie todos os clientes</p>
      </div>
      <div className="clients-card-container">
        <div className="clients-card-header">
          <div className="clients-search">
            <input
              type="text"
              placeholder="Pesquisar por nome ou CNPJ..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
            />
            <FaSearch />
          </div>
          <button className="clients-add-btn" onClick={openNew}>
            <FaPlus /> Novo Cliente
          </button>
        </div>

        {/* --- Main Client Table --- */}
        <table className="clients-table">
          <thead>
            <tr>
              <th>CNPJ</th>
              <th>Cliente</th>
              <th>Produtos Contratados</th>
              <th>Vencimento</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length > 0 ? pageItems.map(c => {
               const clientProducts = c.produtos || [];
              return (
                <tr key={c.id}>
                  <td>{formatCNPJ(c.cnpj)}</td>
                  <td>{c.name}</td>
                  {/* Products, Expiry, Status Columns */}
                  <td>
                     {clientProducts.length > 0 ? (
                        <ul>
                           {clientProducts.map(prod => <li key={prod}>{prod}</li>)}
                        </ul>
                     ) : (
                        'N/A'
                     )}
                  </td>
                  <td>
                     {clientProducts.length > 0 ? (
                         <ul>
                           {clientProducts.map(prod => {
                              const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4);
                              const vencimento = c.productsData?.[prod]?.[`${prefix}_dataVencimento`];
                              return <li key={`${prod}-venc`}>{formatDate(vencimento) || 'N/A'}</li>;
                           })}
                        </ul>
                     ) : (
                        'N/A'
                     )}
                  </td>
                   <td>
                     {clientProducts.length > 0 ? (
                         <ul>
                           {clientProducts.map(prod => {
                              const displayStatus = computeProductStatus(prod, c);
                              const cssClass = statusClassMap[displayStatus] || 'status-desconhecido';
                              return (
                                 <li key={`${prod}-stat`}>
                                    <span className={`status ${cssClass}`}>{displayStatus}</span>
                                 </li>
                               );
                           })}
                        </ul>
                     ) : (
                        'N/A'
                     )}
                  </td>

                  <td className="clients-actions">
                    <FaEdit onClick={() => openEdit(c)} style={{ cursor: 'pointer' }}/>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="6">Nenhum cliente encontrado.</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* --- Pagination --- */}
        <div className="clients-pagination">
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>Anterior</button>
          <span className="page-info">Página {page + 1} de {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(p + 1, pageCount - 1))} disabled={page + 1 >= pageCount}>Próxima</button>
        </div>
      </div>

      {/* --- Modal Form --- */}
      {modalOpen && (
        <div className="clients-modal">
          <div className="clients-modal-content">
             {/* Wrap form for potential onSubmit validation */}
             <form onSubmit={(e) => { e.preventDefault(); saveClient(); }}>
                <h3>
                {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
                <FaTimes className="clients-close" onClick={() => setModalOpen(false)} style={{ cursor: 'pointer', float: 'right' }}/>
                </h3>

                {/* --- Basic Client Info --- */}
                <div className="clients-field">
                <label>CNPJ *</label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="text" name="cnpj" value={formatCNPJ(form.cnpj)} onChange={(e) => setForm({...form, cnpj: e.target.value.replace(/\D/g,'')})} required pattern="[\d.\/-]+" title="Digite um CNPJ válido"/>
                    <button type="button" disabled={loadingCnpj} onClick={fetchCnpjData} style={{ marginLeft: '10px' }}>
                    {loadingCnpj ? 'Buscando...' : 'Buscar Dados'}
                    </button>
                </div>
                 {cnpjError && <small className="error" style={{ color: 'red' }}>{cnpjError}</small>}
                </div>

                 {/* Fields possibly filled by CNPJ lookup */}
                 <div className="form-grid"> {/* Use grid for better layout */}
                    <div className="clients-field">
                       <label>Razão Social *</label>
                       <input type="text" name="name" value={form.name} onChange={handleChange} required />
                    </div>
                    <div className="clients-field">
                       <label>Nome Fantasia</label>
                       <input type="text" name="alias" value={form.alias} onChange={handleChange} />
                    </div>
                     <div className="clients-field">
                       <label>CEP</label>
                       <input type="text" name="zip" value={form.zip} onChange={handleChange} maxLength="9" />
                    </div>
                     <div className="clients-field">
                       <label>Endereço</label>
                       <input type="text" name="street" value={form.street} onChange={handleChange} />
                    </div>
                     <div className="clients-field">
                       <label>Número</label>
                       <input type="text" name="number" value={form.number} onChange={handleChange} />
                    </div>
                    <div className="clients-field">
                       <label>Bairro</label>
                       <input type="text" name="district" value={form.district} onChange={handleChange} />
                    </div>
                    <div className="clients-field">
                       <label>Complemento</label>
                       <input type="text" name="details" value={form.details} onChange={handleChange} />
                    </div>
                    <div className="clients-field">
                       <label>Cidade</label>
                       <input type="text" name="city" value={form.city} onChange={handleChange} />
                    </div>
                    <div className="clients-field">
                       <label>Estado (UF)</label>
                       <input type="text" name="state" value={form.state} onChange={handleChange} maxLength="2"/>
                    </div>
                     <div className="clients-field">
                       <label>Segmento</label>
                        <select name="segmento" value={form.segmento} onChange={handleChange}>
                            <option value="">Selecione...</option>
                            <option>Varejo</option><option>Financeiro</option>
                            <option>Saúde</option><option>Indústrias</option><option>Novos Segmentos</option>
                            <option>Outro</option>
                        </select>
                     </div>
                     <div className="clients-field"><label>Origem</label>
                        <select name="origem" value={form.origem} onChange={handleChange}>
                            <option value="">Selecione...</option>
                            <option>Outbound</option><option>Inbound</option><option>Referral</option>
                            <option>Outro</option>
                        </select>
                     </div>
                     <div className="clients-field"><label>Classificação</label>
                        <select name="classificacao" value={form.classificacao} onChange={handleChange}>
                            <option value="">Selecione...</option>
                            <option>Enterprise</option><option>SMB</option>
                        </select>
                     </div>
                     <div className="clients-field"><label>Slug da Instância</label>
                        <input type="text" name="slugInstancia" value={form.slugInstancia} onChange={handleChange} />
                        <small>Apenas o nome do slug (ex: nomecliente)</small>
                     </div>
                     <div className="clients-field"><label>Token da Instância</label>
                        <input type="text" name="token" value={form.token} onChange={handleChange} />
                        <small>Não utilizar o N45iy03o6EWNfL338c9Y</small>
                     </div>
                     <div className="clients-field"><label>API Token da Instância</label>
                        <input type="text" name="apitoken" value={form.apitoken} onChange={handleChange} />
                     </div>
                 </div>


                {/* --- Responsável Info --- */}
                <h4>Responsável (Empresa Contratante)</h4>
                 <div className="form-grid">
                    <div className="clients-field"><label>Nome</label>
                    <input type="text" name="respEmpresaNome" value={form.respEmpresaNome} onChange={handleChange} />
                    </div>
                    <div className="clients-field"><label>E-mail</label>
                    <input type="email" name="respEmpresaEmail" value={form.respEmpresaEmail} onChange={handleChange} />
                    </div>
                    <div className="clients-field"><label>Área</label>
                    <input type="text" name="respEmpresaArea" value={form.respEmpresaArea} onChange={handleChange} />
                    </div>
                    <div className="clients-field"><label>Cargo</label>
                    <input type="text" name="respEmpresaCargo" value={form.respEmpresaCargo} onChange={handleChange} />
                    </div>
                    <div className="clients-field"><label>Maturidade</label>
                    <select name="respEmpresaMaturidade" value={form.respEmpresaMaturidade} onChange={handleChange}>
                        <option>Básico</option><option>Intermediário</option><option>Avançado</option>
                    </select>
                    </div>
                 </div>

                 {/* --- Product Selection (Checkboxes) --- */}
                <h4>Produto(s) Contratado(s) *</h4>
                <div className="clients-field product-checkbox-group">
                    {AVAILABLE_PRODUCTS.map(prod => (
                        <div key={prod}>
                            <input
                                type="checkbox"
                                id={`prod-${prod.replace(/\s+/g, '')}`} // Create unique ID
                                name="produtos"
                                value={prod}
                                checked={form.produtos.includes(prod)}
                                onChange={handleProductCheckboxChange}
                            />
                            <label htmlFor={`prod-${prod.replace(/\s+/g, '')}`}>{prod}</label>
                        </div>
                    ))}
                    {form.produtos.length === 0 && <small className="error" style={{ color: 'red', display: 'block' }}>Selecione ao menos um produto.</small>}
                </div>


                {/* --- Dynamic Product Forms --- */}
                {form.produtos.map(productName => {
                    const prefix = productName.toLowerCase().split(' ')[0].substring(0, 4);
                    const productData = form.productsData[productName] || {}; // Get data for this product

                    // Helper to get value, handling potential undefined
                    const getVal = (fieldName) => productData[fieldName] ?? '';
                    const getNumVal = (fieldName) => productData[fieldName] ?? 0;

                    return (
                        <div key={productName} className="product-section">
                            <h4>— {productName}</h4>
                            <div className="form-grid">
                                <div className="clients-field">
                                    <label>Link do Contrato</label>
                                    <input type="text" name={`${prefix}_linkContrato`} data-product={productName} value={getVal(`${prefix}_linkContrato`)} onChange={handleProductChange} />
                                </div>
                                <div className="clients-field">
                                    <label>Data de Vencimento *</label>
                                    <input type="date" name={`${prefix}_dataVencimento`} data-product={productName} value={getVal(`${prefix}_dataVencimento`)} onChange={handleProductChange} required/>
                                </div>
                                <div className="clients-field">
                                    <label>Status *</label>
                                    <select name={`${prefix}_status`} data-product={productName} value={getVal(`${prefix}_status`)} onChange={handleProductChange} required>
                                        <option>Ativo</option>
                                        <option>Em Renegociação</option>
                                        <option>Churn</option>
                                        {/* Vencido is calculated, maybe don't allow selecting it? Or allow if needed. */}
                                        <option>Vencido</option>
                                    </select>
                                </div>

                                {/* Common Financial/Contract Fields */}
                                {productName !== 'Experience Awards' && productName !== 'Consultoria' && ( // These have simpler payment structures
                                    <>
                                       <div className="clients-field"><label>Revisado Cláusulas?</label>
                                          <select name={`${prefix}_revisadoClausulas`} data-product={productName} value={getVal(`${prefix}_revisadoClausulas`)} onChange={handleProductChange}>
                                            <option>Não</option><option>Sim</option>
                                          </select>
                                       </div>
                                    </>
                                )}
                                <div className="clients-field"><label>Tipo de Renovação</label>
                                  <select name={`${prefix}_tipoRenovacao`} data-product={productName} value={getVal(`${prefix}_tipoRenovacao`)} onChange={handleProductChange}>
                                    {productName !== 'CX Platform' && <option>Igual a CX Platform</option>}
                                    <option>Aditivo</option><option>Automático</option>
                                  </select>
                                </div>
                                <div className="clients-field"><label>Índice de Renovação</label>
                                   <select name={`${prefix}_indiceRenovacao`} data-product={productName} value={getVal(`${prefix}_indiceRenovacao`)} onChange={handleProductChange}>
                                    {productName !== 'CX Platform' && <option>Igual a CX Platform</option>}
                                    <option>IPCA</option><option>IGP-M</option><option>Menor entre IPCA/IGPM</option>
                                  </select>
                                </div>
                                <div className="clients-field">
  <label>Competência 1º Faturamento</label>
  <input
    type="month"
    name={`${prefix}_competenciaPrimeiroFaturamento`}
    data-product={productName}
    // Gera sempre “YYYY-MM” ou string vazia
    value={(() => {
      const comp = getVal(
        `${prefix}_competenciaPrimeiroFaturamento`
      ); // ex: "3/2024" ou "03/2024"
      if (!comp) return "";
      const [rawMonth, rawYear] = comp.split("/").map((s) =>
        s.trim()
      );
      const monthNum = parseInt(rawMonth, 10);
      // mês de 1 a 12
      if (isNaN(monthNum) || !rawYear) return "";
      const mm = String(monthNum).padStart(2, "0"); // “03”
      return `${rawYear}-${mm}`;                  // “2024-03”
    })()}
    onChange={(e) => {
      const [year, month] = e.target.value.split("-");
      if (!year || !month) return;
      const formatted = `${month}/${year}`; // “MM/YYYY”
      // dispara só o nome e o valor formatado
      handleProductChange({
        target: {
          name: e.target.name,
          value: formatted,
          dataset: e.target.dataset,
        },
      });
    }}
  />
</div>


                                <div className="clients-field"><label>Forma de Pagamento</label>
                                   <select name={`${prefix}_formaPagamento`} data-product={productName} value={getVal(`${prefix}_formaPagamento`)} onChange={handleProductChange}>
                                     {productName !== 'CX Platform' && <option>Igual a CX Platform</option>}
                                     <option>Boleto</option><option>Transferência</option>
                                  </select>
                                </div>
                                <div className="clients-field"><label>Condição de pagamento - Tipo</label>
                                    <select name={`${prefix}_condicaoPagamentoTipo`} data-product={productName} value={getVal(`${prefix}_condicaoPagamentoTipo`)} onChange={handleProductChange}>
                                       {productName !== 'CX Platform' && <option>Igual a CX Platform</option>}
                                        <option>Todo dia X</option>
                                        <option>X Dias após a emissão da NF</option>
                                    </select>
                                </div>
                                <div className="clients-field"><label>Condição de pagamento - Valor</label>
                                    <input
                                        type="text" // Use text to allow any input, validation happens elsewhere or on save
                                        name={`${prefix}_condicaoPagamentoValor`}
                                        data-product={productName}
                                        value={getVal(`${prefix}_condicaoPagamentoValor`)}
                                        onChange={handleProductChange}
                                        placeholder="Ex: 15 (dia ou nº de dias)"
                                        // Make required only if the type demands it
                                        required={getVal(`${prefix}_condicaoPagamentoTipo`) === 'Todo dia X'}
                                    />
                                     {getVal(`${prefix}_condicaoPagamentoTipo`) === 'Todo dia X' && !getVal(`${prefix}_condicaoPagamentoValor`) && <small className="error" style={{color: 'red'}}>Obrigatório</small>}
                                </div>
                                <div className="clients-field"><label>E-mail(s) p/ Envio da NF</label>
                                    <input type="text" name={`${prefix}_emailsNF`} data-product={productName} value={getVal(`${prefix}_emailsNF`)} onChange={handleProductChange} />
                                </div>
                                <div className="clients-field field-full-width"><label>Outras informações envio NF</label>
                                    <textarea name={`${prefix}_outrasInfosEnvio`} data-product={productName} value={getVal(`${prefix}_outrasInfosEnvio`)} onChange={handleProductChange} />
                                </div>
                            </div> {/* End form-grid */}

                            {/* Pricing - Varies by product */}
                             {['CX Platform', 'Customer Insights', 'Employee Experience'].includes(productName) && (
                                <>
                                    <h5>Precificação Acordada ({productName})</h5>
                                    <div className="form-grid">
                                        <div className="clients-field"><label>Tipo de Precificação</label>
                                            <select name={`${prefix}_tipoPrecificacao`} data-product={productName} value={getVal(`${prefix}_tipoPrecificacao`)} onChange={handleProductChange}>
                                                <option>PRECIFICAÇÃO FIXA</option>
                                                <option>TICKET MÍNIMO</option>
                                                <option>POR DATA DE AVALIAÇÃO RESPONDIDAS (100% Variável)</option>
                                            </select>
                                        </div>
                                        <div className="clients-field"><label>Valor do Ticket</label>
                                            <input type="text" name={`${prefix}_valorTicket`} data-product={productName} value={formatCurrency(getNumVal(`${prefix}_valorTicket`))} onChange={handleProductCurrencyChange}/>
                                        </div>
                                        {productName !== 'Customer Insights' && ( // CI doesn't have disparo option? Verify this.
                                             <div className="clients-field"><label>Formato Contratação</label>
                                                <select name={`${prefix}_formatoContratacao`} data-product={productName} value={getVal(`${prefix}_formatoContratacao`)} onChange={handleProductChange}>
                                                    <option>Resposta</option><option>Disparo</option>
                                                </select>
                                            </div>
                                        )}
                                        <div className="clients-field"><label>Volume Contratado</label> {/* Changed label slightly */}
                                            <input type="text" name={`${prefix}_volumeResposta`} data-product={productName} value={getVal(`${prefix}_volumeResposta`)} onChange={handleProductChange} placeholder="Ex: 10000 / mês"/>
                                        </div>
                                        <div className="clients-field"><label>Quantidade mín./máx.</label>
                                            <input type="text" name={`${prefix}_quantidadeMinMax`} data-product={productName} value={getVal(`${prefix}_quantidadeMinMax`)} onChange={handleProductChange} placeholder="Ex: min 500 / max 2000"/>
                                        </div>
                                        <div className="clients-field"><label>Valor Adicional</label> {/* Changed label slightly */}
                                            <input type="text" name={`${prefix}_valorRespostaAdicional`} data-product={productName} value={formatCurrency(getNumVal(`${prefix}_valorRespostaAdicional`))} onChange={handleProductCurrencyChange}/>
                                        </div>
                                        <div className="clients-field"><label>Limitação Orçamento?</label>
                                            <select name={`${prefix}_limiteOrcamento`} data-product={productName} value={getVal(`${prefix}_limiteOrcamento`)} onChange={handleProductChange}>
                                                <option>Não</option><option>Sim</option>
                                            </select>
                                            <small>Se Sim, OPS controla na calculadora</small>
                                        </div>
                                    </div>
                                </>
                             )}

                            {/* Tag Sense - Only for CX Platform and Employee Experience */}
                            {['CX Platform', 'Employee Experience'].includes(productName) && (
                                <>
                                    <h5>Tag Sense ({productName})</h5>
                                    <div className="form-grid">


                                    <div className="clients-field"><label>Tem TagSense?</label>
                                            <select name={`${prefix}_TagSense`} data-product={productName} value={getVal(`${prefix}_TagSense`)} onChange={handleProductChange}>
                                                <option>Não</option><option>Sim</option>
                                            </select>
                                        </div>
                                         {/* Show these only if Setup is Sim */}
                                         {getVal(`${prefix}_TagSense`) === 'Sim' && (
                                             <>



                                        <div className="clients-field"><label>Tipo de TAG SENSE?</label>
                                            <select name={`${prefix}_tipoTagSense`} data-product={productName} value={getVal(`${prefix}_tipoTagSense`)} onChange={handleProductChange}>
                                                <option>Padrão</option> {/* Implies No Customization */}
                                                <option>Customizado</option> {/* Implies Yes Customization */}
                                            </select>
                                        </div>
              
                                                <div className="clients-field"><label>Modelo</label>
                                                    <select name={`${prefix}_modeloTagSense`} data-product={productName} value={getVal(`${prefix}_modeloTagSense`)} onChange={handleProductChange}>
                                                        <option>3.5</option><option>4</option><option>SoluCX</option>
                                                    </select>
                                                </div>
                                                <div className="clients-field"><label>Volume mensal comentários</label>
                                                    <input type="text" name={`${prefix}_volumeMensalComentarios`} data-product={productName} value={getVal(`${prefix}_volumeMensalComentarios`)} onChange={handleProductChange}/>
                                                </div>
                                                <div className="clients-field"><label>Valor cobrado tag sense</label>
                                                    <input type="text" name={`${prefix}_valorTagSense`} data-product={productName} value={formatCurrency(getNumVal(`${prefix}_valorTagSense`))} onChange={handleProductCurrencyChange}/>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                             )}

                            {/* Setup - For CX, CI, EE */}
                            {['CX Platform', 'Customer Insights', 'Employee Experience'].includes(productName) && (
                                <>
                                    <h5>Setup ({productName})</h5>
                                    <div className="form-grid">
                                        <div className="clients-field"><label>Tem SETUP?</label>
                                            <select name={`${prefix}_setup`} data-product={productName} value={getVal(`${prefix}_setup`)} onChange={handleProductChange}>
                                                <option>Não</option><option>Sim</option>
                                            </select>
                                        </div>
                                         {/* Show these only if Setup is Sim */}
                                         {getVal(`${prefix}_setup`) === 'Sim' && (
                                             <>
                                                <div className="clients-field"><label>Valor do Setup</label>
                                                    <input type="text" name={`${prefix}_valorSetup`} data-product={productName} value={formatCurrency(getNumVal(`${prefix}_valorSetup`))} onChange={handleProductCurrencyChange}/>
                                                </div>
                                                <div className="clients-field"><label>Data Emissão SETUP</label>
                                                    <input type="date" name={`${prefix}_dataEmissaoSetup`} data-product={productName} value={getVal(`${prefix}_dataEmissaoSetup`)} onChange={handleProductChange}/>
                                                </div>
                                             </>
                                         )}
                                    </div>
                                </>
                            )}

                            {/* Experience Awards Specific */}
                            {productName === 'Experience Awards' && (
                                <>
                                    <h5>Detalhes ({productName})</h5>
                                    <div className="form-grid">
                                        <div className="clients-field"><label>Pacote</label>
                                          <select name={`${prefix}_pacote`} data-product={productName} value={getVal(`${prefix}_pacote`)} onChange={handleProductChange}>
                                            <option>Report</option><option>Live</option>
                                          </select>
                                        </div>
                                        <div className="clients-field"><label>Tipo de Pagamento</label>
                                            <select name={`${prefix}_tipoPagamento`} data-product={productName} value={getVal(`${prefix}_tipoPagamento`)} onChange={handleProductChange}>
                                                <option>Pagamento Único</option><option>Parcelado</option>
                                            </select>
                                        </div>
                                        <div className="clients-field"><label>Valor</label>
                                            <input type="text" name={`${prefix}_valor`} data-product={productName} value={formatCurrency(getNumVal(`${prefix}_valor`))} onChange={handleProductCurrencyChange}/>
                                        </div>
                                        <div className="clients-field"><label>Se parcelado, vezes?</label>
                                            <input type="number" name={`${prefix}_parcelas`} data-product={productName} value={getNumVal(`${prefix}_parcelas`)} onChange={handleProductChange} min="1"/>
                                        </div>
                                        <div className="clients-field"><label>Stakeholders</label>
                                            <input type="text" name={`${prefix}_stakeholders`} data-product={productName} value={getVal(`${prefix}_stakeholders`)} onChange={handleProductChange}/>
                                        </div>
                                        <div className="clients-field"><label>Categorias Liberadas</label>
                                            <input type="text" name={`${prefix}_categoriasLiberadas`} data-product={productName} value={getVal(`${prefix}_categoriasLiberadas`)} onChange={handleProductChange}/>
                                        </div>
                                        <div className="clients-field"><label>Anos Liberados</label>
                                            <input type="text" name={`${prefix}_anosLiberados`} data-product={productName} value={getVal(`${prefix}_anosLiberados`)} onChange={handleProductChange}/>
                                        </div>
                                        <div className="clients-field"><label>Quem Receberá Acesso?</label>
                                            <input type="text" name={`${prefix}_quemReceberaAcesso`} data-product={productName} value={getVal(`${prefix}_quemReceberaAcesso`)} onChange={handleProductChange}/>
                                        </div>
                                    </div>
                                </>
                            )}

                             {/* Consultoria Specific */}
                            {productName === 'Consultoria' && (
                                <>
                                    <h5>Detalhes ({productName})</h5>
                                    <div className="form-grid">
                                        <div className="clients-field"><label>Tipo de Pagamento</label>
                                            <select name={`${prefix}_tipoPagamento`} data-product={productName} value={getVal(`${prefix}_tipoPagamento`)} onChange={handleProductChange}>
                                                <option>Pagamento Único</option><option>Parcelado</option>
                                            </select>
                                        </div>
                                        <div className="clients-field"><label>Valor</label>
                                            <input type="text" name={`${prefix}_valor`} data-product={productName} value={formatCurrency(getNumVal(`${prefix}_valor`))} onChange={handleProductCurrencyChange}/>
                                        </div>
                                        <div className="clients-field"><label>Se parcelado, vezes?</label>
                                            <input type="number" name={`${prefix}_parcelas`} data-product={productName} value={getNumVal(`${prefix}_parcelas`)} onChange={handleProductChange} min="1"/>
                                        </div>
                                    </div>
                                </>
                            )}


                        </div> // End product-section
                    );
                })} {/* End product map */}

                {/* --- Horas (Global) --- */}
                 {form.produtos.length > 0 && ( // Show only if products are selected
                    <>
                        <h4>Quantidade de Horas (Geral)</h4>
                        <div className="form-grid">
                            <div className="clients-field">
                                <label>Horas de CS</label>
                                <input type="number" name="hs_csQuantidade" value={form.hs_csQuantidade} onChange={handleChange} />
                                <select name="hs_csPeriodicidade" value={form.hs_csPeriodicidade} onChange={handleChange}>
                                    <option>Mês</option><option>Trimestre</option><option>Pacote Único</option>
                                </select>
                            </div>
                            <div className="clients-field">
                                <label>Horas de Suporte/PS</label>
                                <input type="number" name="hs_psQuantidade" value={form.hs_psQuantidade} onChange={handleChange} />
                                <select name="hs_psPeriodicidade" value={form.hs_psPeriodicidade} onChange={handleChange}>
                                    <option>Mês</option><option>Trimestre</option><option>Pacote Único</option>
                                </select>
                            </div>
                        </div>
                    </>
                 )}

                {/* --- Responsáveis Internos --- */}
                 {form.produtos.length > 0 && (
                     <>
                        <h4>Responsáveis Internos</h4>
                         <div className="form-grid">
                            <div className="clients-field">
                                <label>Responsável CS</label>
                                {/* TODO: Replace input with a select populated from users list */}
                                <input type="email" name="responsavelCS" value={form.responsavelCS} onChange={handleChange} placeholder="email@empresa.com"/>
                            </div>
                            <div className="clients-field">
                                <label>Responsável Comercial</label>
                                {/* TODO: Replace input with a select populated from users list */}
                                <input type="email" name="responsavelComercial" value={form.responsavelComercial} onChange={handleChange} placeholder="email@empresa.com" readOnly={!isEdit && user?.email} title={!isEdit ? "Preenchido automaticamente na criação" : ""}/>
                            </div>
                         </div>
                     </>
                 )}


                {/* --- Action Button --- */}
                 <div className="clients-field form-actions">
                    <button type="submit" className="clients-save-btn" disabled={form.produtos.length === 0}>
                        {isEdit ? 'Atualizar Cliente' : 'Salvar Novo Cliente'}
                    </button>
                 </div>

                {/* --- History (Timeline) --- */}
                 {isEdit && (
                     <div className="clients-history-updates">
                        <h4>Histórico de Atualizações</h4>
                        <ul>
                            {(form.historicoUpdates || []).slice().reverse().map((h, i) => { // Show newest first
                                // Attempt to display changes more clearly
                                const changesDescription = Object.entries(h.changes || {})
                                .map(([key, value]) => {
                                    // Handle complex objects like product data - just indicate change
                                    if (typeof value === 'object' && value !== null) {
                                        return `${key} foi atualizado`;
                                    }
                                    // Use formatChangeValue for basic fields
                                    return `${key}: ${formatChangeValue(key, value)}`;
                                })
                                .join(' | ');

                                return (
                                    <li key={i}>
                                    <strong>{h.email}</strong>
                                    <span style={{ marginLeft: '10px', marginRight: '10px' }}>
                                        {h.timestamp?.seconds
                                            ? new Date(h.timestamp.seconds * 1000).toLocaleString('pt-BR')
                                            : 'Data indisponível'
                                        }
                                    </span>
                                    <span style={{ display: 'block', marginTop: '4px', fontSize: '0.9em' }}>{changesDescription || 'Nenhuma alteração registrada'}</span>
                                    </li>
                                );
                            })}
                        </ul>
                     </div>
                 )}
             </form> {/* End Form */}
          </div> {/* End Modal Content */}
        </div> /* End Modal */
      )}
    </div> /* End Wrapper */
  );
};

export default Clients;